import { useQuery } from "@tanstack/react-query";
import type { ActivityCategory, RecommendationSource } from "@/lib/enums";
import { recommendationGenerationKeys } from "@/lib/query";

export interface GeneratedSuggestion {
  id: string | null;
  title: string;
  note: string | null;
  category: string | null;
}

export interface GenerateSuggestionsResult {
  source: RecommendationSource;
  suggestions: GeneratedSuggestion[];
}

/**
 * EPIC-009: fires POST /api/pto-entries/:entryId/suggestions/generate
 * whenever both an entry and a category are known — ephemeral, nothing is
 * persisted server-side (see src/api/recommendation-generation.ts). Callers
 * gate rendering on `entryId`/`category` both being set; `enabled` is the
 * belt-and-suspenders guard.
 */
export function useGenerateSuggestions(entryId: string, category: ActivityCategory) {
  return useQuery({
    queryKey: recommendationGenerationKeys.forEntry(entryId, category),
    enabled: Boolean(entryId && category),
    queryFn: async (): Promise<GenerateSuggestionsResult> => {
      const res = await fetch(`/api/pto-entries/${entryId}/suggestions/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Failed to generate suggestions");
      }
      const json = await res.json();
      return json.data;
    },
  });
}
